import React, { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';

import {
  DistanceUnit,
  MeasurementSystem,
  PageTotal,
  PageTotalsDisplayOptions,
  StepsUnit,
  TimeUnit,
} from '../../../types/__generated__';
import {
  getDistanceUnitForMeasurementSystem,
  getMeasurementSystemFromSettings,
} from '../../utils/measurementSystem';
import { FormattedMeasurement } from '../Formatters/FormattedMeasurement';
import { FilterButtons } from './FilterButtons/FilterButtons';
import {
  ActivityFilters,
  ActivityTotal,
  buildFilteredTotals,
  mapMeasurementTypeToActivityTotalMeasurementType,
} from './filters';

import styles from './ActivityTotals.scss';
import { useFitnessManagerContext } from '../FitnessManager/FitnessManagerContext';

interface ActivityTotalsProps {
  showFilterButtons: boolean;
  showOverallCompleted: boolean;
}

export const ActivityTotals = ({
  showFilterButtons,
  showOverallCompleted,
}: ActivityTotalsProps) => {
  const [activityFilter, setActivityFilter] = useState(ActivityFilters.all.key);

  const { t } = useTranslation('common', { useSuspense: false });
  const { settings, totals, displayOptions } = useFitnessManagerContext();

  if (
    displayOptions === PageTotalsDisplayOptions.DoNotDisplay ||
    !settings.showTotaliser
  ) {
    return null;
  }

  const primaryMeasurementTypeToUse = mapMeasurementTypeToActivityTotalMeasurementType(
    totals.primaryMeasurementType,
  );

  const measurementSystem = getMeasurementSystemFromSettings(settings);

  const { activityTypes, activityTotalsMap } = buildViewData(
    totals.timeline,
    measurementSystem,
    activityFilter,
    setActivityFilter,
  );

  const allTotal = activityTotalsMap[ActivityFilters.all.key];
  const allPrimaryMeasurement = allTotal?.[primaryMeasurementTypeToUse];

  if (!allPrimaryMeasurement?.value) {
    return null;
  }

  const total = activityTotalsMap[activityFilter];
  const primaryMeasurement = total[primaryMeasurementTypeToUse];

  return (
    <div>
      {showFilterButtons && (
        <FilterButtons
          activityTypes={activityTypes}
          activityFilter={activityFilter}
          setActivityFilter={setActivityFilter}
        />
      )}
      <div className={classNames('jg-text--regular-light', styles.summary)}>
        <strong>
          <FormattedMeasurement measurement={primaryMeasurement} />
        </strong>
        {showOverallCompleted &&
          activityFilter === ActivityFilters.all.key &&
          ` ${t('activity.totals.completedOverall')}`}
      </div>
    </div>
  );
};

const buildViewData = (
  timelineTotals: PageTotal[],
  measurementSystem: MeasurementSystem,
  activityFilter: string,
  setActivityFilter: Dispatch<SetStateAction<string>>,
) => {
  if (timelineTotals.length === 0) {
    return {
      activityTypes: [ActivityFilters.all.key],
      activityTotalsMap: {
        [ActivityFilters.all.key]: buildDefaultZeroTotal(
          getDistanceUnitForMeasurementSystem(measurementSystem),
        ),
      },
    };
  } else {
    const {
      activityTotalsMap,
      activityTypes,
      isSingleActivityMode,
    } = buildFilteredTotals(measurementSystem, timelineTotals);

    if (
      isSingleActivityMode &&
      activityFilter === ActivityFilters.all.key &&
      activityTypes.length > 0
    ) {
      // The initial value of activityFilter is "all".
      // However, we remove "all" if there is only one activity type.
      // So, switch the current filter over to that one type.
      setActivityFilter(activityTypes[0]);
    }

    return {
      activityTypes,
      activityTotalsMap,
    };
  }
};

const buildDefaultZeroTotal = (distanceUnit: DistanceUnit): ActivityTotal => ({
  distance: {
    unit: distanceUnit,
    value: 0,
  },
  duration: {
    unit: TimeUnit.Seconds,
    value: 0,
  },
  elevation: {
    unit: distanceUnit,
    value: 0,
  },
  steps: {
    unit: StepsUnit.Steps,
    value: 0,
  },
});
